'use client';

import { useState, type CSSProperties, type ReactNode } from 'react';

import Link from '~/(tenant)/_components/tenant-link';

import { Check, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { currencyMinorUnits, formatCurrency } from '@kit/shared/currency';
import { CurrencyIcon, useTenantCurrency } from '@kit/ui/currency-icon';
import { Trans } from '@kit/ui/trans';

import { DetailsDrawer } from '~/(tenant)/_components/details-drawer';
import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import { DirectionalArrow, Eyebrow } from '~/(tenant)/_components/native/_primitives';

import type { AvailableMembershipPlan } from '../_lib/server/available-memberships.loader';
import { selectMembershipPlanCopy } from '../_lib/select-plan-copy';
import { MembershipPlanPerks } from './membership-plan-perks';
import {
  MembershipPlanBenefits,
  MembershipPlanHero,
  useMembershipPeriodLabel,
} from './membership-plan-showcase';

const BENEFITS_PREVIEW_COUNT = 4;
const DESCRIPTION_PREVIEW_LINES = 3;
// Rough char budget for `DESCRIPTION_PREVIEW_LINES` at fontSize 13 / lineHeight
// 1.55 inside a ~280px card content width. Above this the CSS line-clamp will
// truncate, so we surface "View details" to let the user open the drawer.
// Below it the description fits in full and the button would be misleading.
const DESCRIPTION_CLAMP_CHARS = 180;

const PER_PERIOD_SUFFIX_KEYS: Record<string, string> = {
  monthly: 'common:memberClub.memberships.purchase.perMonth',
  quarterly: 'common:memberClub.memberships.purchase.perQuarter',
  semi_annual: 'common:memberClub.memberships.purchase.perSemester',
  annual: 'common:memberClub.memberships.purchase.perYear',
};

const PER_PERIOD_SUFFIX_DEFAULTS: Record<string, string> = {
  monthly: '/ month',
  quarterly: '/ quarter',
  semi_annual: '/ 6 months',
  annual: '/ year',
};

/**
 * Editorial purchase card for a single membership plan. Mirrors the
 * NativePurchasePackageCard layout so the two surfaces (packages /
 * memberships) feel like one product. Renders a wide hero image at the top
 * when `image_url` is set; falls back to a colored rail otherwise.
 *
 * Public component — the surrounding tab is reachable without sign-in.
 */
export function NativePurchaseMembershipCard({
  plan,
  selectable = false,
  selected = false,
  onSelect,
  onPurchase,
  onWalletPurchase,
  walletBalance,
  isPurchasing = false,
  rateOptions,
}: {
  plan: AvailableMembershipPlan;
  rateOptions?: ReactNode;
  /**
   * When true, the card acts as a tap-to-select radio rather than a discovery
   * card with its own "Become a Member" CTA. Used by the /membership intake
   * picker so the same component drives both the marketing catalog and the
   * application form's plan selector.
   */
  selectable?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  /**
   * Self-serve purchase handler. When provided AND the plan is self-serve
   * (requires_approval === false), the CTA becomes a button that launches the
   * inline checkout instead of linking to the /membership intake form. Approval
   * plans always link to intake regardless.
   */
  onPurchase?: (plan: AvailableMembershipPlan) => void;
  onWalletPurchase?: (plan: AvailableMembershipPlan) => void;
  walletBalance?: number;
  isPurchasing?: boolean;
}) {
  const t = useNativeTokens();
  const currency = useTenantCurrency();
  const { i18n, t: translate } = useTranslation();
  const [detailsOpen, setDetailsOpen] = useState(false);

  const localized = selectMembershipPlanCopy(
    {
      description: plan.description,
      description_alt: plan.description_alt,
      benefits: plan.benefits,
      benefits_alt: plan.benefits_alt,
    },
    i18n.language,
  );

  const isLifetime = plan.period === 'lifetime';

  const periodLabel = useMembershipPeriodLabel(plan.period);

  const perPeriodSuffix = isLifetime
    ? ''
    : translate(PER_PERIOD_SUFFIX_KEYS[plan.period] ?? '', {
        defaultValue: PER_PERIOD_SUFFIX_DEFAULTS[plan.period] ?? '',
      });

  const accent = plan.color || t.foreground;

  const previewBenefits = localized.benefits.slice(0, BENEFITS_PREVIEW_COUNT);
  // Only surface "View details" when there is actually hidden content — extra
  // benefits beyond the preview cap OR a description long enough that the
  // line-clamp will truncate it. Short descriptions show in full on the card,
  // so opening a drawer to read the same text would be pointless friction.
  const hasMore =
    localized.benefits.length > BENEFITS_PREVIEW_COUNT ||
    (localized.description?.length ?? 0) > DESCRIPTION_CLAMP_CHARS;

  const cardStyle: CSSProperties = {
    background: t.background,
    border: `1px solid ${selectable && selected ? t.foreground : t.line}`,
    borderRadius: t.radius,
    marginBottom: 14,
    overflow: 'hidden',
    position: 'relative',
    boxShadow:
      selectable && selected ? `inset 0 0 0 1px ${t.foreground}` : 'none',
    cursor: selectable ? 'pointer' : 'default',
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
    textAlign: 'start',
    width: '100%',
    color: t.foreground,
    fontFamily: 'inherit',
    padding: 0,
  };

  const handleCardClick = selectable && onSelect ? onSelect : undefined;

  const planNameId = `plan-${plan.id}-name`;

  return (
    <div
      style={cardStyle}
      onClick={handleCardClick}
      data-test="native-purchase-membership-card"
      data-plan-id={plan.id}
      data-native-selection-row={selectable || undefined}
      data-selected={selectable && selected ? 'true' : undefined}
    >
      {selectable ? (
        <input
          type="radio"
          name="membership-plan"
          value={plan.id}
          checked={selected}
          onChange={() => onSelect?.()}
          aria-labelledby={planNameId}
          style={{
            position: 'absolute',
            opacity: 0,
            pointerEvents: 'none',
            // Keep the radio in the layout so focus rings paint near the card.
            width: 1,
            height: 1,
            margin: 0,
          }}
          data-test="native-purchase-membership-radio"
        />
      ) : null}
      <MembershipPlanHero
        name={plan.name}
        imageUrl={plan.image_url}
        accent={accent}
      />

      <div style={{ padding: '24px 22px 22px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 12,
            marginBottom: 6,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            {periodLabel ? (
              <Eyebrow size="sm" style={{ marginBottom: 6 }}>
                {periodLabel}
              </Eyebrow>
            ) : null}
            <div
              id={planNameId}
              style={{
                fontFamily: t.fontDisplay,
                fontSize: 24,
                fontWeight: 400,
                color: t.foreground,
                lineHeight: 1.1,
                letterSpacing: '-0.01em',
              }}
            >
              {plan.name}
            </div>
          </div>
          {isLifetime ? (
            <span
              style={{
                padding: '5px 10px',
                background: `${t.foreground}10`,
                fontFamily: t.fontMono,
                fontSize: 8,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: t.foreground,
                flexShrink: 0,
              }}
            >
              <Trans
                i18nKey="common:memberClub.memberships.card.periodLifetime"
                defaults="Lifetime"
              />
            </span>
          ) : null}
        </div>

        {localized.description ? (
          <p
            dir="auto"
            style={{
              margin: '12px 0 0',
              fontFamily: t.fontSans,
              fontSize: 13,
              color: t.muted,
              lineHeight: 1.55,
              display: '-webkit-box',
              WebkitLineClamp: DESCRIPTION_PREVIEW_LINES,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {localized.description}
          </p>
        ) : null}

        {rateOptions}

        {/* Price block */}
        <div
          style={{
            marginTop: 22,
            paddingTop: 20,
            borderTop: `1px solid ${t.line}`,
            display: 'flex',
            alignItems: 'flex-end',
            gap: 12,
          }}
        >
          <CurrencyIcon
            style={{
              width: 26,
              height: 26,
              strokeWidth: 1.5,
              color: t.foreground,
              alignSelf: 'center',
            }}
          />
          <div
            style={{
              fontFamily: t.fontDisplay,
              fontSize: 48,
              fontWeight: 300,
              color: t.foreground,
              lineHeight: 0.95,
              letterSpacing: '-0.02em',
            }}
          >
            {formatCurrency(Number(plan.price), currency, i18n.language, {
              style: 'decimal',
              maximumFractionDigits: currencyMinorUnits(currency),
            })}
          </div>
          {perPeriodSuffix ? (
            <span
              style={{
                fontFamily: t.fontSans,
                fontSize: 12,
                color: t.muted,
                paddingBottom: 6,
                letterSpacing: '0.02em',
              }}
            >
              {perPeriodSuffix}
            </span>
          ) : null}
        </div>

        {/* Benefits list (preview only — full list lives in the drawer) */}
        <MembershipPlanBenefits benefits={previewBenefits} accent={accent} />

        {/* Included perk packages — granted free on activation. Read-only;
            renders nothing when the plan has no perks. */}
        <MembershipPlanPerks perks={plan.perks ?? []} accent={accent} />

        {hasMore ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setDetailsOpen(true);
            }}
            style={{
              marginTop: 14,
              background: 'transparent',
              border: `1px solid ${t.foreground}33`,
              padding: '10px 14px',
              cursor: 'pointer',
              fontFamily: t.fontMono,
              fontSize: 10,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: t.foreground,
              borderRadius: t.radius,
              width: '100%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              fontWeight: 500,
            }}
            data-test="native-purchase-membership-view-details"
          >
            <Trans
              i18nKey="common:memberClub.memberships.purchase.viewDetails"
              defaults="View details"
            />
          </button>
        ) : null}

        {plan.founder_seats_remaining !== undefined ? (
          <p
            style={{ marginTop: 14, fontFamily: t.fontSans, fontSize: 12, color: t.muted }}
            data-test="native-purchase-membership-founder-seats"
          >
            <Trans
              i18nKey="common:memberClub.memberships.purchase.founderSeats"
              defaults="Founder pricing · {{count}} spots left"
              values={{ count: plan.founder_seats_remaining }}
            />
          </p>
        ) : null}

        {selectable ? (
          /* Decorative pill: click bubbles to the card's onClick (or the visually-hidden
             radio's onChange after the a11y fix). Not interactive on its own. */
          <div
            style={{
              width: '100%',
              marginTop: 22,
              padding: '15px 0',
              background: selected ? t.accent : 'transparent',
              color: selected ? t.background : t.foreground,
              border: `1px solid ${selected ? t.accent : `${t.foreground}33`}`,
              borderRadius: t.radius,
              fontFamily: t.fontSans,
              fontSize: 11,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              transition: 'background-color 150ms ease, color 150ms ease',
            }}
            data-test="native-purchase-membership-select-pill"
          >
            {selected ? (
              <>
                <Check size={14} strokeWidth={1.8} />
                <Trans
                  i18nKey="common:memberClub.memberships.purchase.selected"
                  defaults="Selected"
                />
              </>
            ) : (
              <Trans
                i18nKey="common:memberClub.memberships.purchase.selectPlan"
                defaults="Tap to select"
              />
            )}
          </div>
        ) : plan.founder_seats_remaining === 0 ? (
          <div
            style={{
              width: '100%',
              marginTop: 22,
              padding: '15px 0',
              border: `1px solid ${t.foreground}33`,
              borderRadius: t.radius,
              color: t.muted,
              fontFamily: t.fontSans,
              fontSize: 11,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              textAlign: 'center',
            }}
            data-test="native-purchase-membership-sold-out"
          >
            <Trans
              i18nKey="common:memberClub.memberships.purchase.soldOut"
              defaults="Sold out"
            />
          </div>
        ) : plan.requires_approval === false && onPurchase && !plan.is_family ? (
          <div style={{ marginTop: 22, display: 'grid', gap: 10 }}>
            {onWalletPurchase ? (
              <button
                type="button"
                disabled={isPurchasing}
                onClick={(e) => {
                  e.stopPropagation();
                  onWalletPurchase(plan);
                }}
                style={{
                  width: '100%',
                  padding: '15px 0',
                  background: '#059669',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: t.radius,
                  cursor: isPurchasing ? 'default' : 'pointer',
                  opacity: isPurchasing ? 0.7 : 1,
                  fontFamily: t.fontSans,
                  fontSize: 11,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
                data-test="native-purchase-membership-wallet-cta"
              >
                <Wallet size={14} strokeWidth={1.8} />
                <Trans
                  i18nKey="common:memberClub.memberships.purchase.payWithWallet"
                  defaults="Pay with wallet"
                />
                {walletBalance !== undefined ? (
                  <span style={{ opacity: 0.8 }}>({walletBalance.toFixed(2)})</span>
                ) : null}
              </button>
            ) : null}

            <button
              type="button"
              disabled={isPurchasing}
              onClick={(e) => {
                e.stopPropagation();
                onPurchase(plan);
              }}
              style={{
                width: '100%',
                padding: '15px 0',
                background: t.accent,
                color: t.background,
                border: 'none',
                borderRadius: t.radius,
                cursor: isPurchasing ? 'default' : 'pointer',
                opacity: isPurchasing ? 0.7 : 1,
                fontFamily: t.fontSans,
                fontSize: 11,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
              }}
              data-test="native-purchase-membership-cta"
            >
              {isPurchasing ? (
                <Trans
                  i18nKey="common:memberClub.memberships.purchase.processing"
                  defaults="Processing..."
                />
              ) : (
                <>
                  {Number(plan.price) <= 0 ? (
                    <Trans
                      i18nKey="common:memberClub.memberships.purchase.joinFree"
                      defaults="Join now"
                    />
                  ) : (
                    <Trans
                      i18nKey="common:memberClub.memberships.purchase.becomeMember"
                      defaults="Become a Member"
                    />
                  )}
                  <DirectionalArrow size={12} strokeWidth={1.4} />
                </>
              )}
            </button>
          </div>
        ) : (
          <Link
            href={plan.is_family ? `/memberships/family?plan=${plan.id}` : `/membership?plan=${plan.id}`}
            style={{
              width: '100%',
              marginTop: 22,
              padding: '15px 0',
              background: t.accent,
              color: t.background,
              border: 'none',
              borderRadius: t.radius,
              textDecoration: 'none',
              fontFamily: t.fontSans,
              fontSize: 11,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
            }}
            data-test="native-purchase-membership-cta"
          >
            <Trans
              i18nKey="common:memberClub.memberships.purchase.becomeMember"
              defaults="Become a Member"
            />
            <DirectionalArrow size={12} strokeWidth={1.4} />
          </Link>
        )}
      </div>

      <DetailsDrawer
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        name={plan.name}
        subtitle={periodLabel || undefined}
        description={localized.description}
        features={localized.benefits}
        imageUrl={plan.image_url}
        color={plan.color}
      />
    </div>
  );
}
